// In-app help for every setting (event settings, profile, scheduling): the
// short explanation shown in the help tooltip (desktop) or help sheet (phones),
// and the Help Center article (and section) it opens.
//
//   slug     article at https://www.eventlive.pro/help/<slug>
//            (repo mkrn/eventlive.pro, content/help/articles/<slug>.md)
//   section  optional heading inside that article to scroll to (exact text)
//
// scripts/help-check.mjs fails a deploy when a slug here is not published on
// eventlive.pro/help, and when this file changes without a help review (it is
// a source of the "Event settings" area). scripts/settings-ui-check.mjs fails
// when a screen uses a topic that isn't defined here.
//
// Keep summaries to one or two short sentences: what the setting does for
// guests, and the one thing people get wrong.

export const HELP_SITE = "https://www.eventlive.pro";

export const HELP_TOPICS = {
  // Overview
  "overview.cover": {
    title: "Cover photo",
    summary:
      "The picture guests see on your event page before you go live. Horizontal photos work best.",
    slug: "how-to-change-update-cover-picture-of-the-event",
  },
  "overview.eventLink": {
    title: "Your event link",
    summary:
      "Share this link with guests. They watch in any browser, with no app or account.",
    slug: "how-to-share-event-links-with-guests-without-email-addresses",
  },

  // How to Go Live
  "goLive.rtmpServer": {
    title: "RTMP server",
    summary:
      "Paste this into the Server or URL field of your encoder. It's the same for every event.",
    slug: "how-to-go-live-using-rtmp",
    section: "Where should you use your RTMP Keys?",
  },
  "goLive.streamKey": {
    title: "Stream key",
    summary: "Each event has its own key.",
    slug: "how-to-go-live-using-rtmp",
  },
  "goLive.freeTest": {
    title: "Free test",
    summary:
      "Tests use the same server with a different key, and guests don't see them unless you share the test link.",
    slug: "how-to-test-live-streaming",
  },
  "goLive.watchLink": {
    title: "Link to watch",
    summary:
      "Open it on another device to check picture and sound while you stream.",
    slug: "how-to-test-live-streaming",
  },
  "goLive.delegate": {
    title: "Guest broadcaster",
    summary:
      "The instructions include the stream key for the method you picked above, so send them only to the person streaming.",
    slug: "invite-a-guest-broadcaster-or-videographer-to-go-live",
  },
  "goLive.room": {
    title: "Rooms",
    summary:
      "Each room has its own stream key, recording and guestbook. Guests switch rooms with tabs on the event page.",
    slug: "multi-room-events",
    section: "Stream each room",
  },

  // Guest list
  "guests.list": {
    title: "Guest list",
    summary:
      "Being on the list doesn't mean someone watched: the status shows who visited the page, joined live or watched the replay.",
    slug: "how-to-view-the-list-of-guests-who-opted-in-for-reminders-or-signed-the-guestbook",
  },
  "guests.invite": {
    title: "Invite by email",
    summary:
      "On invite-only events, only guests invited here can watch. To invite many at once, use Import at the top.",
    slug: "how-to-add-guests-to-the-guest-list-and-send-reminders",
  },
  "guests.import": {
    title: "Import a guest list",
    summary:
      "Upload a CSV file with names and emails to invite many guests at once.",
    slug: "importing-a-csv-file-for-event-invitations-and-troubleshooting-import-issues",
  },

  // Title, date, time, event link, uploads
  "details.title": {
    title: "Event title",
    summary: "Shown at the top of your event page and in guest emails.",
    slug: "how-to-change-the-title-of-the-event",
  },
  "details.location": {
    title: "Location",
    summary: "Appears next to the date on your event page.",
    slug: "how-to-change-the-title-of-the-event",
  },
  "details.dateTime": {
    title: "Date and time",
    summary:
      "When you plan to start, in the event's timezone. You can go live up to 1 hour early.",
    slug: "how-to-postpone-change-the-date-of-the-event",
    section: "Change the date, time or timezone",
  },
  "details.timezone": {
    title: "Timezone",
    summary:
      "Where the event takes place. Guests in other places see the time in their own timezone.",
    slug: "why-is-my-event-time-different-from-the-time-i-selected",
  },
  "details.days": {
    title: "Multi-day event",
    summary:
      "Keep one link for 1 to 7 consecutive days. Adding days to an unlocked event may need a payment.",
    slug: "how-to-stream-on-multiple-days-using-the-same-url",
    section: "Add days to an upcoming event",
  },
  "details.eventLink": {
    title: "Event link",
    summary:
      "Choose it before you send invitations. Letters, numbers, dashes and underscores only, and it must not be taken.",
    slug: "how-to-edit-change-the-event-link",
  },

  // Rooms
  "rooms.rename": {
    title: "Room name",
    summary:
      "Use the name guests know, like “Court 2” or “Main stage”. Up to 40 characters.",
    slug: "multi-room-events",
    section: "Rename, switch and manage rooms",
  },

  // Guestbook
  "guestbook.mode": {
    title: "Guest messages",
    summary:
      "Switch any time, even during the event; messages already posted are kept. Guests enter their name and email the first time they post.",
    slug: "guestbook-chat-or-no-messages",
  },
  "guestbook.pinned": {
    title: "Pinned message",
    summary:
      "Good for a welcome note, the schedule or a thank-you. You can pin several; to change one, pin a new message and delete the old one.",
    slug: "how-to-add-and-customize-a-pinned-message-in-the-guestbook",
  },
  "guestbook.messages": {
    title: "Messages",
    summary:
      "Delete a message with its trash button and guests no longer see it. After the event you can download all messages and photos as a PDF.",
    slug: "how-to-delete-comments-from-the-guestbook",
  },

  // Privacy
  "privacy.access": {
    title: "Who can watch",
    summary:
      "Applies to the event page, the replay and website embeds. Ticketed events use tickets instead, set up in Sell Tickets.",
    slug: "how-to-change-event-privacy-settings",
    section: "Who can enter?",
  },
  "privacy.requireDetails": {
    title: "Ask for name and email",
    summary:
      "Guests don't need an account and can turn off reminder emails. Details aren't verified, so you see what they typed.",
    slug: "how-to-require-guests-to-enter-name-and-email-to-access-the-livestream",
  },
  "privacy.liveOnly": {
    title: "Live only",
    summary: "No replay and no rewind. Your recording is kept.",
    slug: "how-to-turn-off-the-replay-option",
  },
  "privacy.download": {
    title: "Download link",
    summary:
      "Allows the guests to download the video. To charge for downloads, use Sell video download in Sell Tickets.",
    slug: "can-guests-download-event-videos",
  },
  "privacy.viewerCount": {
    title: "Viewer counter",
    summary:
      "Only guests stop seeing it: you still see live viewers while streaming and in Analytics.",
    slug: "how-to-change-event-privacy-settings",
    section: "Replay and download",
  },
  "privacy.hidePage": {
    title: "Hide the event page",
    summary:
      "Guests who open its link, embeds or download page see that the host has hidden it. Show the page again to download the video. Nothing is deleted.",
    slug: "how-to-change-event-privacy-settings",
    section: "Temporarily hide the event",
  },
  "privacy.ticketed": {
    title: "Ticketed event",
    summary:
      "To choose who can watch here again, set the pricing to Disable Pay-Per-View in Sell Tickets.",
    slug: "how-to-sell-tickets-for-a-livestream-event",
  },

  // Branding
  "branding.description": {
    title: "Event description",
    summary:
      "Shown under the video on your event page. Use it for the schedule, speakers or practical details.",
    slug: "how-to-add-an-event-description",
  },
  "branding.customDate": {
    title: "Date shown to guests",
    summary:
      "Useful for guests in several time zones. The event still starts, and reminders still go out, at the scheduled time. Clear it to show the scheduled date again.",
    slug: "why-is-my-event-time-different-from-the-time-i-selected",
    section: "Show your own date text",
  },
  "branding.company": {
    title: "Company name and website",
    summary:
      "Guests see the name with your logo, and the website makes it a link. Leave both empty to use your profile's details.",
    slug: "eventlive-white-label-service-availability",
    section: "Add Branding to an Individual Event",
  },
  "branding.logo": {
    title: "Logo",
    summary:
      "To use one logo on all your events, upload it in your profile. A logo here takes priority on this event.",
    slug: "how-to-add-a-custom-logo-to-the-live-streaming-page",
  },

  // Analytics
  "analytics.overview": {
    title: "Analytics",
    summary:
      "Counts are devices, not people, on every player: a phone that moves from Wi-Fi to mobile data counts again.",
    slug: "how-to-see-who-watched-your-event",
  },
  "analytics.viewerHours": {
    title: "Viewer-hours",
    summary:
      "All the video people watched, on any device, app or TV. The report's Watch Time shows the same hours.",
    slug: "what-is-the-maximum-number-of-viewers-allowed-for-a-livestream-on-eventlive",
  },

  // Sell tickets
  "tickets.setupLink": {
    title: "Link to connect Stripe",
    summary: "The link connects the Stripe account to this event only.",
    slug: "how-to-sell-tickets-for-a-livestream-event",
    section: "Set up ticket sales",
  },
  "tickets.savedAccounts": {
    title: "Stripe accounts you've connected before",
    summary:
      "The account you pick gets this event's ticket payouts only; your other events keep theirs.",
    slug: "how-to-sell-tickets-for-a-livestream-event",
    section: "Use a Stripe account from another event",
  },
  "tickets.pricingMode": {
    title: "Pricing",
    summary:
      "Choose Disable Pay-Per-View to stop selling; the event's privacy settings then apply again.",
    slug: "how-to-sell-tickets-for-a-livestream-event",
    section: "Fixed-price ticket options",
  },
  "tickets.singleDay": {
    title: "Single-day tickets",
    summary:
      "Only for multi-day events. Buyers pick the day at checkout, and the all-days ticket stays available.",
    slug: "how-to-sell-tickets-for-a-livestream-event",
    section: "Fixed-price ticket options",
  },
  "tickets.download": {
    title: "Sell video download",
    summary:
      "Buyers get the download once the recording is ready. It works for live-only events too.",
    slug: "how-to-sell-tickets-for-a-livestream-event",
    section: "Fixed-price ticket options",
  },
  "tickets.replayPrice": {
    title: "Replay price",
    summary: "Leave it unset to keep selling the replay at the ticket price.",
    slug: "how-to-sell-tickets-for-a-livestream-event",
    section: "Fixed-price ticket options",
  },
  "tickets.passFees": {
    title: "Buyers pay the fees",
    summary: "Buyers see it as a “service & processing fee” line at checkout.",
    slug: "ppv-event-fees-on-eventlive",
    section: "Who pays?",
  },
  "tickets.rooms": {
    title: "Tickets for rooms",
    summary:
      "With one ticket, buyers register once and switch rooms with the tabs. Separate tickets suit rooms with different audiences.",
    slug: "how-to-sell-tickets-for-a-livestream-event",
    section: "Events with several rooms",
  },

  // Fundraising
  "fundraising.organizations": {
    title: "Organizations",
    summary:
      "Donations go to these nonprofits through Pledge, which sends donors their tax receipts. You can add up to 20.",
    slug: "fundraisers-and-donations-for-nonprofits",
    section: "Set up a fundraiser for your event",
  },
  "fundraising.widget": {
    title: "Donations on the event page",
    summary:
      "Adds Pledge's donation form beside the video (a Donate button on phones). It stays on the replay page after the event.",
    slug: "fundraisers-and-donations-for-nonprofits",
    section: "On the event page",
  },
  "fundraising.overlay": {
    title: "Live donation tracker",
    summary:
      "It's part of the video, so the replay and download show it too, with the totals of that moment. Test streams don't show it. A keyword you add on Pledge appears on it within a few minutes.",
    slug: "fundraisers-and-donations-for-nonprofits",
    section: "Live donation tracker on your stream",
  },
  "fundraising.guestbook": {
    title: "Donations in the guestbook",
    summary:
      "Each donation appears as a message with the donor's name and amount, so guests can thank them.",
    slug: "fundraisers-and-donations-for-nonprofits",
    section: "Donations in the guestbook and live chat",
  },
  "fundraising.pledge": {
    title: "Donate link and Pledge",
    summary:
      "Pledge doesn't email you about it. If the fundraiser isn't in your Pledge account, check that it uses the same email as EventLive, then save here once more.",
    slug: "fundraisers-and-donations-for-nonprofits",
    section: "Manage your fundraiser on Pledge",
  },
  "fundraising.options": {
    title: "Button, goal and suggested amount",
    summary:
      "Leave the goal empty to show only the total raised. The suggested amount is preselected on the donation form.",
    slug: "fundraisers-and-donations-for-nonprofits",
    section: "What donors see",
  },

  // Embed
  "embed.layout": {
    title: "Embed layout",
    summary:
      "Full embed fits most websites. Just video suits pages that already have their own text and buttons.",
    slug: "how-to-embed-eventlive-streams-on-your-website",
    section: "Embed options",
  },
  "embed.options": {
    title: "Code options",
    summary:
      "They aren't saved with the event, so set them each time before you copy the code.",
    slug: "how-to-embed-eventlive-streams-on-your-website",
    section: "Embed options",
  },
  "embed.emailLinks": {
    title: "Email links",
    summary:
      "Pages on your Branding website count on their own. Other pages need your OK, so nobody can put their own page into your guests' emails.",
    slug: "how-to-embed-eventlive-streams-on-your-website",
    section: "Email links",
  },
  "embed.codeType": {
    title: "Code type",
    summary:
      "Wix and Google Sites put custom HTML in a separate frame, so even the script can't see email links there: emails send those viewers to evt.live.",
    slug: "how-to-embed-eventlive-streams-on-your-website",
    section: "Script or simple iframe",
  },

  // Manage Video (for the redesign; the current screen has no help tips yet)
  "video.upload": {
    title: "Upload a video",
    summary:
      "Show a slideshow or intro before you go live, or add a recording after the event.",
    slug: "how-to-add-a-slideshow-or-intro-video-to-your-event",
  },
  "video.replace": {
    title: "Replace the recording",
    summary: "Upload an edited video in place of the live recording.",
    slug: "how-to-replace-an-original-livestream-with-an-edited-version",
  },
  "video.trim": {
    title: "Trim",
    summary: "Cut the start of the recording guests watch.",
    slug: "how-to-trim-a-livestream-video",
  },
  "video.remove": {
    title: "Remove a part",
    summary: "Delete an uploaded video or a section of the recording.",
    slug: "how-to-delete-or-remove-an-uploaded-video",
  },

  // Delete
  "event.delete": {
    title: "Delete event",
    summary:
      "Removes the event, its page and its recording. An unlocked event that hasn't streamed yet can return its credits.",
    slug: "how-to-cancel-a-scheduled-live-stream",
    section: "When does deletion return credits?",
  },

  // Schedule your event (CreateEventForm)
  "create.title": {
    title: "Event title",
    summary:
      "Guests see it at the top of your event page and in their invitations and reminders.",
    slug: "how-to-create-an-event-on-eventlive",
    section: "Event title and link",
  },
  "create.eventLink": {
    title: "Event link",
    summary:
      "Pick it before you send invitations: if you change it later, links you've already shared stop working.",
    slug: "how-to-create-an-event-on-eventlive",
    section: "Event title and link",
  },
  "create.eventType": {
    title: "Event type",
    summary:
      "Guests don't see it. It only tailors what EventLive shows you, like reviews from similar events.",
    slug: "how-to-create-an-event-on-eventlive",
    section: "Event type",
  },
  "create.dateTime": {
    title: "Date and time",
    summary: "You can go live from 1 hour before this time.",
    slug: "how-to-create-an-event-on-eventlive",
    section: "Date, time and time zone",
  },
  "create.days": {
    title: "Multi-day event",
    summary:
      "One link for up to 7 consecutive days. Each day uses one event credit.",
    slug: "how-to-create-an-event-on-eventlive",
    section: "Multi-day events",
  },
  "create.fundraiser": {
    title: "Raise donations",
    summary:
      "Donations go through Pledge, which pays the nonprofit and emails each donor a tax receipt.",
    slug: "how-to-create-an-event-on-eventlive",
    section: "Raise donations for a nonprofit",
  },

  // Profile › Update Profile
  "profile.email": {
    title: "Sign-in email",
    summary: "Each email address can belong to only one EventLive account.",
    slug: "how-to-change-the-email-address-associated-with-your-account",
  },
  "profile.accountType": {
    title: "Account type",
    summary: "Switching is free, and your events stay as they are.",
    slug: "how-to-switch-from-a-personal-account-to-a-business-account",
  },
  "profile.logo": {
    title: "Logo",
    summary:
      "It also appears in the emails your guests get, unless an event has its own logo.",
    slug: "how-to-add-a-custom-logo-to-the-live-streaming-page",
  },
  "profile.noBranding": {
    title: "Remove all branding",
    summary:
      "Applies to every event, even ones with their own logo in Event Branding.",
    slug: "eventlive-white-label-service-availability",
    section: "Remove all branding",
  },

  // Profile › Viewer-hours
  "profile.viewerHours": {
    title: "Viewer-hours",
    summary:
      "Going over 5,000 on an event doesn't stop your stream. Contact us before a large event so we can plan it with you.",
    slug: "what-is-the-maximum-number-of-viewers-allowed-for-a-livestream-on-eventlive",
  },

  // Profile › My Video Wall
  "videoWall.overview": {
    title: "Video Wall",
    summary:
      "Password-protected, invite-only, hidden and test events stay off it. Guests can search it by event title.",
    slug: "video-wall",
  },
  "videoWall.embed": {
    title: "Embed the Video Wall",
    summary:
      "New events appear on it by themselves, so you paste the code only once.",
    slug: "video-wall",
    section: "Embed it on your website",
  },
};

export const helpTopic = (id) => HELP_TOPICS[id] || null;

export const helpArticleUrl = (slug, section) =>
  `${HELP_SITE}/help/${slug}${section ? `#${headingAnchor(section)}` : ""}`;

// Same anchors as the Help Center's headings (eventlive.pro lib/help.ts
// slugifyHeading): "Review the buyer’s total" → "review-the-buyer-s-total".
export const headingAnchor = (heading) => {
  const lower = String(heading || "").toLowerCase();
  // normalize() can be missing on some Hermes builds; it only strips accents.
  const plain = lower.normalize ? lower.normalize("NFKD") : lower;
  return (
    plain
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "")
      .slice(0, 80) || "section"
  );
};

export default HELP_TOPICS;
